import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { StoreApi, useStore } from "zustand";
import AppLoading from "components/AppLoading/AppLoading";
import { CallOutError } from "components/CallOutError/CallOutError";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import {
  useDataTableContext,
  useRefetchDataTableStore,
} from "pages/gdt/store/DataTableProvider";
import {
  createWorkflowEditorStore,
  WorkflowEditorStoreState,
} from "./WorkflowEditor.store";
import assert from "assert";

/**
 * TODOs

Frontend currently calculating outputs from result columns.

Keep/discard intermediates would be by column only then.... Any issues?

users can't delete columns so what happens if they don't discard a result then want to do it later.

We need more stable way for backend to return edges for input nodes.

Need to know whether output is intermediate

Sources that don't trace back to a table or to a table that is not selected...
- Sources that don't map become external sources
- Sources that map to a table that's not selected... Maybe external sources or can we warn a user that they're missing a table?
- NOT FOR NOW JUST MAKE THEM EXTERNAL INPUTS

Will need tool names, not currently available from tables

Param mapping uses tool specs params. Do we also want tool specs for other info to populate?

Do we want to display a list of actual result types in the output policy table or just the output column name?

What about compute resource selection? Do we want resource selectors for every step of the workflow on the workflow table itself?

Should we group workflow table at 3 levels? Tool level, table group level, and column level?

https://www.ag-grid.com/javascript-data-grid/column-groups/



display description for result cols
 */

const WorkflowEditorContext = createContext<
  StoreApi<WorkflowEditorStoreState> | undefined
>(undefined);

export const useWorkflowEditorContext = <T,>(
  selector: (state: WorkflowEditorStoreState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(WorkflowEditorContext);

  if (store === undefined) {
    throw new ContextOutOfBoundsError("WorkflowEditorContext");
  }
  return useStore(store, selector, equalityFn);
};

export interface WorkflowEditorProviderProps {
  children: ReactNode;
  analysisTableIds?: string[];
}

export const WorkflowEditorProvider = ({
  children,
  analysisTableIds,
}: WorkflowEditorProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const [store, setStore] = useState<StoreApi<WorkflowEditorStoreState>>();
  const tableIds = useDataTableContext((s) => {
    if (analysisTableIds === undefined) {
      return [];
    }
    const filteredTables = s.tables.filter((t) => {
      return (
        analysisTableIds.some((id) => id === t.id) && t.kind === "analysis"
      );
    });
    assert(
      filteredTables.length === analysisTableIds.length,
      "Missing tables from store in workflow provider",
    );
    return filteredTables.map(({ id }) => id).sort();
  });

  const refetchDataTableStore = useRefetchDataTableStore();

  useEffect(() => {
    if (store === undefined && loading && !error) {
      createWorkflowEditorStore({
        tableIds,
      })
        .then((store) => setStore(store))
        .catch((err) => setError(err as Error))
        .finally(() => setLoading(false));
    }
  }, [
    store,
    refetchDataTableStore,
    analysisTableIds,
    loading,
    error,
    tableIds,
  ]);

  if (loading) {
    return <AppLoading />;
  }

  if (error !== undefined || store === undefined) {
    return <CallOutError message={error?.message ?? "Unknown Error"} />;
  }

  return (
    <WorkflowEditorContext.Provider value={store}>
      {children}
    </WorkflowEditorContext.Provider>
  );
};
